import { app } from '@/main'
import useAuthStore, {
  isPreAuthToken,
  isPreAuthTokenWithCustomAccess,
  isUserToken,
  getTokenType,
  preAuthRouteMap,
  TokenType,
} from '@/stores/authStore'
import { useRouteGuardStore } from '@/stores/routeGuardStore'
import { cleanOAuthParamsFromUrl, handleAuthCodeCallback, OAUTH_QUERY_PARAMS } from '@/composables/useAuth'
import { waitForPeerSessionHydration } from '@/composables/useAuthTabSync'
import { useQueryClient } from '@tanstack/vue-query'
import { trackRouteChange } from '@/utils/userActivity'
import { getDatadogRum } from '@/plugins/datadog'
import type { RouteLocationNormalized, NavigationGuardReturn } from 'vue-router'
import type { Resource } from '@/types/_general'
import type { UserExtendedData } from '@/types/user--extended-data'
import pwa from '@/utils/pwa'

export interface GuardContext {
  authStore: ReturnType<typeof useAuthStore>
  selectedToken: ReturnType<typeof useAuthStore>['selectedToken']
  isAuthenticated: boolean
  isSolicitor: boolean
  isSystemUser: boolean
}

export type GuardResult = NavigationGuardReturn | undefined
export type Guard = (to: RouteLocationNormalized, ctx: GuardContext) => GuardResult | Promise<GuardResult>

function isTokenTypeAllowed(
  currentTokenType: TokenType | null,
  allowedTypes: (TokenType | null)[] | 'any' | undefined
): boolean {
  if (allowedTypes === 'any') return true
  const allowed = allowedTypes ?? [TokenType.UserToken]
  if (allowed.length === 0) return false
  return allowed.includes(currentTokenType)
}

/**
 * Sends the tab to Login with the OAuth params lifted out of the requested address, so a deep link that
 * autostarts a provider still returns to its page. The rest of the address becomes `return-to`.
 */
function loginRedirect(to: RouteLocationNormalized): GuardResult {
  const query: Record<string, string> = {}
  for (const param of OAUTH_QUERY_PARAMS) {
    const value = to.query[param]
    if (typeof value === 'string') query[param] = value
  }

  const returnTo = cleanOAuthParamsFromUrl(to.fullPath)
  if (returnTo !== '/') {
    query['return-to'] = encodeURIComponent(returnTo)
  }

  return { name: 'Login', query }
}

/**
 * A freshly opened tab may still be receiving the session from a peer tab (sessionStorage is per tab;
 * useAuthTabSync carries it over a BroadcastChannel). Nothing decides before that handshake has settled,
 * so every guard after this one reads the hydrated store instead of an empty token list that is about
 * to fill — the pre-auth routing and the creditor onboarding redirect included. Memoized in
 * useAuthTabSync: only the first navigation of an app start without tokens ever waits (≤ 250 ms).
 */
async function guardPeerSessionHydration(_to: RouteLocationNormalized, ctx: GuardContext): Promise<GuardResult> {
  if (ctx.authStore.tokensList.length === 0) await waitForPeerSessionHydration()
}

function guardPreAuthTokenRouting(to: RouteLocationNormalized, ctx: GuardContext): GuardResult {
  const payload = ctx.selectedToken?.payload

  if (isPreAuthTokenWithCustomAccess(payload)) {
    const routeName = preAuthRouteMap[payload.custom_access]

    if (to.name !== routeName) {
      return { name: routeName, query: to.query }
    }
  }
}

function guardCreditorOnboarding(to: RouteLocationNormalized, ctx: GuardContext): GuardResult {
  const payload = ctx.selectedToken?.payload

  if (to.name === 'CreditorOnboarding' && isUserToken(payload) && payload.c_onb !== false) {
    return { name: 'Dashboard' }
  }

  if (
    ctx.isAuthenticated &&
    !ctx.isSystemUser &&
    !ctx.isSolicitor &&
    to.name !== 'CreditorOnboarding' &&
    to.name !== 'SignOut' &&
    isUserToken(payload) &&
    payload.c_onb === false &&
    sessionStorage.getItem('creditor-onboarding-canceled') !== 'true'
  ) {
    return { name: 'CreditorOnboarding' }
  }
}

async function guardOAuthCallback(to: RouteLocationNormalized): Promise<GuardResult> {
  if (typeof to.query.code === 'string' && typeof to.query.state === 'string') {
    try {
      return await handleAuthCodeCallback(to.query.code, to.query.state)
    } catch (error) {
      console.error('Oauth callback failed:', error)
      return { name: 'Login', query: { auth_errors: 'common.login_failed' } }
    }
  }
}

function guardAuthErrorRedirect(to: RouteLocationNormalized): GuardResult {
  if (typeof to.query.auth_errors === 'string' && to.name !== 'Login') {
    return { name: 'Login', query: { auth_errors: to.query.auth_errors } }
  }
}

function guardUserIdSwitching(to: RouteLocationNormalized, ctx: GuardContext): GuardResult {
  if (typeof to.query.user_id !== 'string') return

  // guardPeerSessionHydration ran first: the requested user is checked against the peer tab's session,
  // not against an empty list, so the tab never silently continues as the other user.
  const requestedUserId = to.query.user_id
  const matchingToken = ctx.authStore.tokensList.find(
    (t) => isUserToken(t.payload) && t.payload.u_id === requestedUserId
  )

  if (matchingToken != null) {
    if (!matchingToken.selected) {
      ctx.authStore.selectToken(matchingToken.token)
    }
  } else if (ctx.authStore.selectedToken != null) {
    ctx.authStore.setTokenList([])
    return loginRedirect(to)
  }
}

function guardAllowedTokenTypes(to: RouteLocationNormalized, ctx: GuardContext): GuardResult {
  // The context is created after guardPeerSessionHydration settled, so a null type here is a genuinely
  // signed-out tab — never a peer session that is still on its way.
  const currentTokenType = getTokenType(ctx.selectedToken?.payload)

  if (!isTokenTypeAllowed(currentTokenType, to.meta.allowedTokenTypes)) {
    if (currentTokenType == null) {
      return loginRedirect(to)
    }

    return { name: 'Dashboard' }
  }
}

async function guardRouteLeave(): Promise<GuardResult> {
  const routeGuardStore = useRouteGuardStore()

  if (!routeGuardStore.isEngaged) return

  return new Promise<GuardResult>((resolve) => {
    routeGuardStore.setPendingNavigation(resolve)
  })
}

async function guardResourceAccess(to: RouteLocationNormalized): Promise<GuardResult> {
  if (to.name !== 'ItemDisplay' && to.name !== 'ItemSearch') return

  if (typeof to.params.resource !== 'string' || to.params.resource.trim() === '') {
    console.warn('guardResourceAccess: Missing or invalid resource parameter, redirecting to Dashboard')
    return { name: 'Dashboard' }
  }

  const resource = to.params.resource as Resource

  return app.runWithContext(async () => {
    const queryClient = useQueryClient()
    const { queryFn } = await import('@/composables/api')

    let userExtendedData: UserExtendedData
    try {
      userExtendedData = await queryClient.query({
        queryKey: ['get', '/user--extended-data', {}] as const,
        queryFn,
      })
    } catch (error) {
      console.error(`guardResourceAccess: Failed to fetch user extended data for resource "${resource}"`, error)
      return { name: 'Dashboard' }
    }

    const hasAccess = userExtendedData.access_rights?.[resource]?.methods?.includes('GET') ?? false

    if (!hasAccess) {
      console.warn(
        `guardResourceAccess: User does not have GET access to resource "${resource}", redirecting to Dashboard`
      )
      return { name: 'Dashboard' }
    }
    return
  })
}

export const allGuards: Guard[] = [
  guardPeerSessionHydration,
  guardRouteLeave,
  guardPreAuthTokenRouting,
  guardOAuthCallback,
  guardAuthErrorRedirect,
  guardUserIdSwitching,
  guardCreditorOnboarding,
  guardAllowedTokenTypes,
  guardResourceAccess,
]

export const tokenValidationGuards: Guard[] = [
  guardPreAuthTokenRouting,
  guardAllowedTokenTypes,
  guardCreditorOnboarding,
  guardResourceAccess,
]

function createGuardContext(authStore: ReturnType<typeof useAuthStore>): GuardContext {
  const selectedToken = authStore.selectedToken
  const payload = selectedToken?.payload

  return {
    authStore,
    selectedToken,
    isAuthenticated: selectedToken != null && !isPreAuthToken(payload),
    isSolicitor: isUserToken(payload) && payload.solicitor === true,
    isSystemUser: isUserToken(payload) && payload.system_user === true,
  }
}

/**
 * Validates a route against guards. Returns early with a redirect/denial if any guard fails,
 * or undefined if all guards pass. Accepts a subset of guards.
 */
export async function validateRouteGuards(
  route: RouteLocationNormalized,
  guards: Guard[] = allGuards
): Promise<GuardResult> {
  const authStore = useAuthStore()

  // A fresh context per guard, as setupRouterGuards does: a guard that waits (peer-session hydration)
  // must not leave the ones after it deciding on a snapshot taken before the wait.
  for (const guard of guards) {
    const result = await guard(route, createGuardContext(authStore))
    if (result != null) return result
  }
}

/**
 * Registers all route guards and hooks with the router.
 * Call this once during app initialization.
 */
export function setupRouterGuards(router: ReturnType<typeof import('vue-router').createRouter>) {
  for (const guard of allGuards) {
    router.beforeEach(async (to) => {
      const authStore = useAuthStore()
      const ctx = createGuardContext(authStore)
      const guardResult = await guard(to, ctx)
      if (guardResult != null) return guardResult
      return true
    })
  }

  router.afterEach(async (to) => {
    if (pwa.needRefresh.value) {
      console.log('New version of the app is available, refreshing to update')
      await pwa.updateServiceWorker()
      window.location.reload()
      return
    }
    const routeName = typeof to.name === 'string' ? to.name : 'unknown'
    const resource = typeof to.params.resource === 'string' ? to.params.resource : undefined
    trackRouteChange(routeName, resource)

    // Datadog RUM view tracking
    let viewName = typeof to.name === 'string' ? to.name : to.path

    if (viewName === 'ItemDisplay' && to.params.view === 'custom') {
      viewName = 'ItemDisplayCustom'
    }

    if (typeof to.params.resource === 'string') {
      viewName = `${viewName}:${to.params.resource}`
    }

    getDatadogRum()?.startView({
      name: viewName,
      context: to.params,
    })
  })
}
